import { useState, useEffect } from 'react';
import axiosInstance from '../config/axios';
import { User, AuthContextReturn } from '../context/AuthContext';
import { Tenant } from '../components/TenantSelectionModal';
import LocalStorageService from '../core/services/LocalStorageService';
import tokenRefreshService from '../core/services/TokenRefreshService';

/**
 * Custom hook for authentication and tenant selection state
 */
const useAuth = (): AuthContextReturn => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [user, setUser] = useState<User | null>(null);
  const [needsTenant, setNeedsTenant] = useState<boolean>(false);            // no tenants at all
  const [needsTenantSelection, setNeedsTenantSelection] = useState<boolean>(false); // multiple tenants
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState<boolean>(false);
  const [authError, setAuthError] = useState<string>('');

  // On mount, check auth; on unmount, stop token refresh
  useEffect(() => {
    checkAuth();

    return () => {
      tokenRefreshService.stop();
    };
  }, []);

  const checkAuth = async (): Promise<boolean> => {
    setIsLoading(true);
    setAuthError('');
    const token = LocalStorageService.getAccessToken();
    if (!token) {
      setIsAuthenticated(false);
      setIsLoading(false);
      return false;
    }
    try {
      // fetch basic user
      const userRes = await axiosInstance.get('/api/user/');
      const userData: User = userRes.data;
      setUser(userData);
      setIsPlatformAdmin(userData.is_platform_admin === true);
      // FIXME: This atually does not make sense before tenant selection. The information should than be stored in the tenant object.!
      // if (!userData.is_super_admin || !userData.is_work_area_admin) {
      //   setIsAdmin(false);
      //   setAuthError('Only administrators can access this application.');
      //   setIsLoading(false);
      //   return false;
      // }
      setIsAdmin(true);
      // handle tenants
      const list: Tenant[] = userData.tenant_list || [];
      if (list.length === 0 && userData.is_platform_admin === true) {
        setNeedsTenant(false);
        setNeedsTenantSelection(false);
        setSelectedTenant(null);
      } else if (list.length === 0) {
        setNeedsTenant(true);
        setNeedsTenantSelection(false);
        setSelectedTenant(null);
      } else if (list.length === 1) {
        setNeedsTenant(false);
        setNeedsTenantSelection(false);
        setSelectedTenant(list[0]);
        LocalStorageService.setSelectedTenantId(list[0].tenant_id);
      } else {
        // multiple tenants available
        setNeedsTenant(false);
        // try to restore previous selection
        const storedId = LocalStorageService.getSelectedTenantId();
        if (storedId) {
          const found = list.find((t: Tenant) => String(t.tenant_id) === storedId);
          if (found) {
            setSelectedTenant(found);
            setNeedsTenantSelection(false);
          } else {
            setNeedsTenantSelection(true);
            setSelectedTenant(null);
          }
        } else {
          setNeedsTenantSelection(true);
          setSelectedTenant(null);
        }
      }
      setIsAuthenticated(true);
      setIsLoading(false);

      // Start proactive token refresh
      tokenRefreshService.start();

      return true;
    } catch (err) {
      console.error(err);
      setIsAuthenticated(false);
      setIsLoading(false);
      return false;
    }
  };

  // invoked when user selects tenant
  const selectTenant = (tenant: Tenant): void => {
    setSelectedTenant(tenant);
    setNeedsTenantSelection(false);
    LocalStorageService.setSelectedTenantId(tenant.tenant_id);
  };

  const logout = (): void => {
    // Stop proactive token refresh
    tokenRefreshService.stop();

    LocalStorageService.clearAuthData();
    setIsAuthenticated(false);
    setUser(null);
    setNeedsTenant(false);
    setNeedsTenantSelection(false);
    setSelectedTenant(null);
    setIsAdmin(false);
    setIsPlatformAdmin(false);
    setAuthError('');
  };

  return {
    isAuthenticated,
    isLoading,
    user,
    needsTenant,
    needsTenantSelection,
    selectedTenant,
    isAdmin,
    isPlatformAdmin,
    authError,
    checkAuth,
    selectTenant,
    logout
  };
};

export default useAuth; 
